import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Scissors, CheckCircle2, Circle, BellRing, Flag } from 'lucide-react';
import { Button } from '../ui/button';
import { methodFor, methodLabel, reminderSchedule } from '../../utils/splitMethods';

// Methods without a step guide still get tracked (outcome only)
const guideFor = (s) => methodFor(s.split_method) || { name: methodLabel(s.split_method, s.split_method_name), steps: [] };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SPLIT_OUTCOMES = [
  { id: 'mated_laying', label: 'Queen mated & laying', cls: 'border-lime-400 text-lime-800 bg-lime-50' },
  { id: 'failed', label: 'Queen failed', cls: 'border-rose-300 text-rose-700 bg-rose-50' },
  { id: 'swarmed', label: 'Swarmed / absconded', cls: 'border-amber-300 text-amber-800 bg-amber-50' },
  { id: 'combined', label: 'Combined back', cls: 'border-sky-300 text-sky-800 bg-sky-50' },
];

const OutcomePicker = ({ split, onOutcome, busy }) => (
  <div className="mt-3 border-t border-amber-200 pt-3" data-testid={`split-outcome-${split.id}`}>
    <p className="text-sm font-medium text-stone-700 mb-2">How did it turn out?</p>
    <div className="flex flex-wrap gap-2">
      {SPLIT_OUTCOMES.map(o => (
        <button key={o.id} disabled={busy} onClick={() => onOutcome(split, o)} data-testid={`split-outcome-${split.id}-${o.id}`}
          className={`text-xs font-semibold rounded-full border px-3 py-1.5 transition-transform hover:-translate-y-0.5 ${o.cls}`}>
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

const fmt = (d) => new Date(String(d).length <= 10 ? `${d}T12:00:00` : d).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

const SplitCard = ({ split, method, reminder, onToggle, onWeek, onFinish, onOutcome, busy }) => {
  const finished = split.status === 'completed';
  const done = split.done_steps || [];
  const current = method.steps.findIndex((_, i) => !done.includes(i));
  return (
    <div className="border border-amber-200 rounded-xl p-4 bg-amber-50/40" data-testid={`split-tracker-${split.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-stone-800">{method.name}</p>
          <p className="text-xs text-stone-500">
            Started {fmt(split.start_date)} · {split.destination_hive_id === split.parent_hive_id ? 'kept in this hive' : `Hive ${split.parent_hive_number ?? '?'} → Hive ${split.destination_hive_number || split.daughter_display_number || 'new'}`}
          </p>
        </div>
        {method.steps.length > 0 && <span className="text-xs font-semibold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5" data-testid={`split-progress-${split.id}`}>
          {done.length}/{method.steps.length} steps
        </span>}
      </div>
      {reminder && (
        <p className="mt-2 text-sm text-stone-700 flex items-center gap-1.5" data-testid={`split-next-reminder-${split.id}`}>
          <BellRing className="h-4 w-4 text-amber-600" /> Next reminder: <strong>{fmt(`${reminder.date}T12:00:00`)}</strong>
          <span className="text-stone-500 truncate">— {reminder.description.split(' — ')[0].replace(`${method.name}: `, '')}</span>
        </p>
      )}
      {finished && <p className="mt-2 text-xs font-semibold text-stone-500">Split finished: record the result below.</p>}
      {!finished && <ul className="mt-3 space-y-1.5">
        {method.steps.map((step, i) => {
          const isDone = done.includes(i);
          const weeks = reminderSchedule(method).filter(r => r.stepIndex === i && r.label);
          return (
            <li key={i}>
              <button disabled={busy} onClick={() => onToggle(split, i)} data-testid={`split-step-${split.id}-${i}`}
                className={`w-full flex items-start gap-2 text-left rounded-lg px-2 py-1.5 transition-colors hover:bg-white ${i === current ? 'bg-white ring-1 ring-amber-300' : ''}`}>
                {isDone ? <CheckCircle2 className="h-5 w-5 text-lime-600 shrink-0" /> : <Circle className="h-5 w-5 text-stone-300 shrink-0" />}
                <span className={`text-sm ${isDone ? 'line-through text-stone-400' : 'text-stone-800'}`}>
                  {step.title} <span className="text-xs text-stone-500">· {step.timing}</span>
                  {i === current && <span className="ml-1 text-[10px] font-bold uppercase text-amber-700">Current</span>}
                </span>
              </button>
              {weeks.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pl-9 pt-1" data-testid={`split-weeks-${split.id}-${i}`}>
                  {weeks.map((w, n) => {
                    const key = `${i}-${n + 1}`;
                    const ticked = (split.done_checks || []).includes(key);
                    const d = new Date(`${String(split.start_date).slice(0, 10)}T12:00:00`);
                    d.setDate(d.getDate() + w.day);
                    return (
                      <button key={key} disabled={busy} onClick={() => onWeek(split, i, n + 1, weeks.length)} data-testid={`split-week-${split.id}-${key}`}
                        className={`inline-flex items-center gap-1 text-xs rounded-full border px-2.5 py-1 transition-colors ${ticked ? 'bg-lime-50 border-lime-400 text-lime-800' : 'bg-white border-stone-200 text-stone-600 hover:border-amber-400'}`}>
                        {ticked ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                        Week {n + 1} · {isNaN(d) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                      </button>
                    );
                  })}
                </div>
              )}
            </li>
          );
        })}
      </ul>}
      {!finished && (
        <div className="mt-3 flex justify-end">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onFinish(split)} data-testid={`split-finish-${split.id}`}>
            <Flag className="h-4 w-4 mr-1" /> Mark split finished
          </Button>
        </div>
      )}
      <OutcomePicker split={split} onOutcome={onOutcome} busy={busy} />
    </div>
  );
};

export const SplitProgressTracker = ({ hiveId }) => {
  const [splits, setSplits] = useState([]);
  const [past, setPast] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const [sRes, pRes] = await Promise.all([
      axios.get(`${API}/hive-splits?hive_id=${hiveId}`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/planned-actions`, { headers }).catch(() => ({ data: [] })),
    ]);
    const all = (sRes.data || []).filter(s => guideFor(s));
    setSplits(all.filter(s => s.status === 'in_progress' || (s.status === 'completed' && !s.outcome)));
    setPast(all.filter(s => s.outcome).slice(0, 5));
    setReminders((pRes.data || []).filter(a => a.action_type === 'split_step' && a.status !== 'completed'));
  }, [hiveId]);

  useEffect(() => { load(); }, [load]);
  // Opened from a Hives list split badge: bring the tracker into view
  useEffect(() => {
    if (!splits.length || new URLSearchParams(window.location.search).get('focus') !== 'splits') return;
    document.querySelector('[data-testid="split-progress-tracker"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [splits.length]);
  useEffect(() => {
    window.addEventListener('refreshData', load);
    return () => window.removeEventListener('refreshData', load);
  }, [load]);

  const save = async (split, body, msg) => {
    setBusy(true);
    try {
      await axios.put(`${API}/hive-splits/${split.id}`, body, { headers });
      toast.success(msg);
      await load();
      window.dispatchEvent(new Event('refreshData'));
    } catch { toast.error('Could not update the split'); } finally { setBusy(false); }
  };

  const onToggle = (split, i) => {
    const done = split.done_steps || [];
    const next = done.includes(i) ? done.filter(x => x !== i) : [...done, i].sort((a, b) => a - b);
    const method = guideFor(split);
    const allDone = next.length === method.steps.length;
    // Ticking a weekly step ticks all its weeks; un-ticking clears them
    const weekCount = reminderSchedule(method).filter(r => r.stepIndex === i && r.label).length;
    const others = (split.done_checks || []).filter(k => !k.startsWith(`${i}-`));
    const checks = weekCount ? { done_checks: next.includes(i) ? [...others, ...Array.from({ length: weekCount }, (_, n) => `${i}-${n + 1}`)] : others } : {};
    save(split, { done_steps: next, ...checks, current_step: method.steps.findIndex((_, k) => !next.includes(k)), ...(allDone ? { status: 'completed', completed_at: new Date().toISOString() } : {}) },
      allDone ? `${method.name} finished` : done.includes(i) ? 'Step un-ticked' : `Step done: ${method.steps[i].title}`);
  };

  // Tick one week of a weekly step; the step is done once every week is ticked
  const onWeek = (split, i, week, weekCount) => {
    const key = `${i}-${week}`;
    const checks = split.done_checks || [];
    const nextChecks = checks.includes(key) ? checks.filter(k => k !== key) : [...checks, key];
    const allWeeks = Array.from({ length: weekCount }, (_, n) => `${i}-${n + 1}`).every(k => nextChecks.includes(k));
    const done = (split.done_steps || []).filter(x => x !== i);
    const nextDone = allWeeks ? [...done, i].sort((a, b) => a - b) : done;
    const method = guideFor(split);
    const allDone = nextDone.length === method.steps.length;
    save(split, { done_checks: nextChecks, done_steps: nextDone, current_step: method.steps.findIndex((_, k) => !nextDone.includes(k)),
      ...(allDone ? { status: 'completed', completed_at: new Date().toISOString() } : {}) },
      allDone ? `${method.name} finished` : checks.includes(key) ? `Week ${week} un-ticked` : `Week ${week} check done`);
  };

  const onFinish = (split) => save(split, { status: 'completed', completed_at: new Date().toISOString() }, 'Split marked finished. Its remaining reminders are cleared.');

  const onOutcome = (split, o) => save(split, { outcome: o.id, outcome_date: new Date().toISOString(), status: 'completed',
    ...(split.status !== 'completed' ? { completed_at: new Date().toISOString() } : {}) }, `Result saved: ${o.label}`);

  if (!splits.length && !past.length) return null;

  return (
    <div data-testid="split-progress-tracker">
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 space-y-3">
        <h3 className="font-bold text-stone-800 flex items-center gap-2">
          <Scissors className="h-5 w-5 text-amber-600" /> Splits
        </h3>
        {splits.map(split => {
          const mine = reminders.filter(r => r.split_id ? r.split_id === split.id : r.hive_id === split.destination_hive_id && r.description?.startsWith(guideFor(split).name));
          const next = mine.sort((a, b) => (a.date || '').localeCompare(b.date || ''))[0];
          return <SplitCard key={split.id} split={split} method={guideFor(split)} reminder={next} onToggle={onToggle} onWeek={onWeek} onFinish={onFinish} onOutcome={onOutcome} busy={busy} />;
        })}
        {past.length > 0 && (
          <div data-testid="split-past-results">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1.5">Past results</p>
            <ul className="space-y-1">
              {past.map(sp => {
                const o = SPLIT_OUTCOMES.find(x => x.id === sp.outcome);
                return (
                  <li key={sp.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`split-past-${sp.id}`}>
                    <span className="text-stone-700">{guideFor(sp).name} · {fmt(sp.start_date)}</span>
                    <span className={`text-xs font-semibold rounded-full border px-2 py-0.5 ${o?.cls || ''}`}>{o?.label || sp.outcome}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default SplitProgressTracker;
